import { useState } from "react";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type {
  EmailOptInBreakdown,
  OptInRecipientSample,
} from "@/types/EmailOptInBreakdown";
import { StandaloneGuestDetail } from "@/components/PGuestDetails/StandaloneGuestDetail";
import type { SendClass } from "@/lib/email/sendClass";

interface OptInWarningBlockProps {
  breakdown: EmailOptInBreakdown | null;
  loading: boolean;
  /**
   * When set, tapping a name in an expanded bucket opens the guest-detail
   * popup (above the confirm modal). Expansion itself only needs the
   * server-provided sample lists on `breakdown`.
   */
  eventId?: string;
  /**
   * The class of the send this block describes. ORGANISATIONAL reaches the
   * opted-out and the no-consent guests alike, so both buckets collapse into
   * one neutral line saying exactly that — "will be skipped" and "GDPR risk"
   * would both be false for such a send. Absent ⇒ MARKETING.
   */
  sendClass?: SendClass;
}

/** "First Last", falling back to the email, then a dash. */
const sampleName = (r: OptInRecipientSample): string =>
  [r.firstName, r.lastName].filter(Boolean).join(" ") ||
  r.email ||
  "—";

interface NoticeRowProps {
  tone: "info" | "error";
  text: string;
  /** Exact bucket count (sample is capped server-side at 100). */
  total: number;
  sample?: OptInRecipientSample[];
  showLabel: string;
  hideLabel: string;
  moreLabel: (count: number) => string;
  onOpenGuest?: (guestId: string) => void;
}

/**
 * One consent-warning notice. When the breakdown carries a sample for its
 * bucket the row grows a show/hide toggle that expands the affected guests
 * in an internally-scrolling list (the popup itself never outgrows the
 * viewport), each name opening the guest-detail popup when clickable.
 */
const NoticeRow: React.FC<NoticeRowProps> = ({
  tone,
  text,
  total,
  sample,
  showLabel,
  hideLabel,
  moreLabel,
  onOpenGuest,
}) => {
  const [open, setOpen] = useState(false);
  const expandable = !!sample && sample.length > 0;
  const isError = tone === "error";
  const Chevron = open ? IconChevronUp : IconChevronDown;
  const Icon = isError ? IconAlertTriangle : IconInfoCircle;

  return (
    <div
      className={`border rounded-md ${
        isError ? "surface-error outline-error" : "surface-secondary outline-quaternary"
      }`}
    >
      <div className="px-3 py-2 flex items-center gap-2">
        <Icon
          size={14}
          className={`shrink-0 ${isError ? "font-error" : "font-tertiary"}`}
        />
        <span
          className={`body-3-medium flex-1 ${isError ? "font-error" : "font-secondary"}`}
        >
          {text}
        </span>
        {expandable && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
            className={`shrink-0 flex items-center gap-0.5 body-3-medium cursor-pointer hover:underline ${
              isError ? "font-error" : "font-secondary"
            }`}
          >
            {open ? hideLabel : showLabel}
            <Chevron size={14} className="shrink-0" />
          </button>
        )}
      </div>
      {expandable && open && (
        <ul
          className={`border-t px-3 py-2 space-y-1 max-h-56 overflow-y-auto ${
            isError ? "outline-error" : "outline-quaternary"
          }`}
        >
          {sample.map((r, i) => {
            const name = sampleName(r);
            const clickable = !!onOpenGuest && !!r.guestId;
            return (
              <li
                key={r.guestId || i}
                className="flex items-baseline gap-2 min-w-0"
              >
                {clickable ? (
                  <button
                    type="button"
                    onClick={() => onOpenGuest(r.guestId!)}
                    className="body-3-medium font-primary cursor-pointer hover:underline truncate text-left"
                  >
                    {name}
                  </button>
                ) : (
                  <span className="body-3-medium font-primary truncate">
                    {name}
                  </span>
                )}
                {r.email && name !== r.email && (
                  <span className="body-3-regular font-tertiary truncate">
                    {r.email}
                  </span>
                )}
              </li>
            );
          })}
          {total > sample.length && (
            <li className="body-3-regular font-tertiary">
              {moreLabel(total - sample.length)}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

/**
 * Pre-send consent warning (plucky-branding-sentinel / opt-in gate). Shows,
 * for the prospective marketing recipients:
 *  - opted-out → auto-skipped (compliant, neutral info line);
 *  - no recorded consent → WILL receive (GDPR/deliverability risk warning).
 * Renders nothing when both are 0. Display-only — the backend enforces the
 * actual skip. Uses the existing send-flow namespace + warning styling.
 * When the breakdown carries sample lists, each notice expands to the
 * affected guests; with `eventId` the names open the guest-detail popup.
 */
export const OptInWarningBlock: React.FC<OptInWarningBlockProps> = ({
  breakdown,
  loading,
  eventId,
  sendClass = "MARKETING",
}) => {
  const t = useTranslations("pSendEmailValidation");
  const [detailGuestId, setDetailGuestId] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="surface-secondary border outline-quaternary rounded-md px-3 py-2 flex items-center gap-2">
        <IconInfoCircle size={14} className="font-tertiary shrink-0" />
        <span className="body-3-medium font-tertiary">
          {t("optInWarningLoading")}
        </span>
      </div>
    );
  }

  if (!breakdown) return null;
  const { optedOutWithEmail, noConsentWithEmail } = breakdown;
  if (optedOutWithEmail <= 0 && noConsentWithEmail <= 0) return null;

  const openGuest = eventId
    ? (guestId: string) => setDetailGuestId(guestId)
    : undefined;
  const moreLabel = (count: number) => t("optInListMore", { count });

  if (sendClass === "ORGANISATIONAL") {
    // The two buckets are disjoint (`false` vs null/absent), so their sum is
    // the exact number of guests this class reaches beyond a marketing send.
    const sample = [
      ...(breakdown.optedOutSample ?? []),
      ...(breakdown.noConsentSample ?? []),
    ];
    return (
      <div className="space-y-2" data-testid="optin-organisational-notice">
        <NoticeRow
          tone="info"
          text={t("organisationalConsentNotice")}
          total={optedOutWithEmail + noConsentWithEmail}
          sample={sample.length > 0 ? sample : undefined}
          showLabel={t("optInListShow")}
          hideLabel={t("optInListHide")}
          moreLabel={moreLabel}
          onOpenGuest={openGuest}
        />
        {eventId && detailGuestId && (
          <StandaloneGuestDetail
            eventId={eventId}
            guestId={detailGuestId}
            onClose={() => setDetailGuestId(null)}
            zClass="z-[10070]"
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {optedOutWithEmail > 0 && (
        <NoticeRow
          tone="info"
          text={t("optedOutSkipNotice", { count: optedOutWithEmail })}
          total={optedOutWithEmail}
          sample={breakdown.optedOutSample}
          showLabel={t("optInListShow")}
          hideLabel={t("optInListHide")}
          moreLabel={moreLabel}
          onOpenGuest={openGuest}
        />
      )}
      {noConsentWithEmail > 0 && (
        <NoticeRow
          tone="error"
          text={t("noConsentWarning", { count: noConsentWithEmail })}
          total={noConsentWithEmail}
          sample={breakdown.noConsentSample}
          showLabel={t("optInListShow")}
          hideLabel={t("optInListHide")}
          moreLabel={moreLabel}
          onOpenGuest={openGuest}
        />
      )}
      {eventId && detailGuestId && (
        <StandaloneGuestDetail
          eventId={eventId}
          guestId={detailGuestId}
          onClose={() => setDetailGuestId(null)}
          zClass="z-[10070]"
        />
      )}
    </div>
  );
};
